<script>
import { toast } from "vue3-toastify";
import { ref as dbRef, get } from "firebase/database";
import { useInstance } from "@/features/composables/useInstance";
import { SYNC_ORIGINS } from "@/features/shared/utils/constants";
import { db } from "@/firebase.js";

export default {
  
    methods: {
        getSyncEnabledOrigins() {
            const { isArgentinaInstance, isMexicoInstance, isSpainInstance } = useInstance();
            
            if (isArgentinaInstance()) {
                return SYNC_ORIGINS.ARGENTINA;
            } else if (isMexicoInstance()) {
                return SYNC_ORIGINS.MEXICO;
            } else if (isSpainInstance()) {
                return SYNC_ORIGINS.SPAIN;
            }
            
            // Fallback para otras instancias - no sincroniza nada
            return SYNC_ORIGINS.DEFAULT;
        },
        validateSelectedFile(file) {
            if (file === null || file === undefined) {
                toast.error("Por favor seleccione un archivo para importar.");
                this.$store.errorLoading(this);
                return false;
            }
            return true;
        },       
        validateCuit(cuit) {
            if (!cuit || cuit.length !== 11) return false;
            
            // Verificar que sean solo números
            if (!/^\d{11}$/.test(cuit)) return false;
            
            // Algoritmo de validación de CUIT
            const multiplicadores = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
            let suma = 0;
            
            for (let i = 0; i < 10; i++) {
            suma += parseInt(cuit[i]) * multiplicadores[i];
            }
            
            const resto = suma % 11;
            const digitoVerificador = resto < 2 ? resto : 11 - resto;
            
            return parseInt(cuit[10]) === digitoVerificador;
        },
        validateDni(dni) {
            if (!dni || dni.length < 7 || dni.length > 8) return false;
            
            // Verificar que sean solo números
            if (!/^\d+$/.test(dni)) return false;
            
            // Verificar rango válido (1.000.000 a 99.999.999)
            const dniNumber = parseInt(dni);
            return dniNumber >= 1000000 && dniNumber <= 99999999;
        },
        showSyncMenuMessage(needsSync) {
            if (needsSync) toast.success("Se actualizará el catálogo de las plataformas donde este ítem esté cargado")
        },
        hasFdsEnabledConfigs() {
            return this.getEnabledFdsOrigins().length > 0;
        },
        getEnabledFdsOrigins() {
            const profileShops = this.$store.getProfileShops;
            if (!profileShops || !profileShops[0] || !Array.isArray(profileShops[0].fdsConfigs)) {
                return [];
            }

            return profileShops[0].fdsConfigs
                .filter(config => config?.isEnabled === true && config?.fdsShopCode && config.fdsShopCode.trim() !== "")
                .map(config => (config?.sourceLabel || "").toLowerCase().trim())
                .filter(Boolean);
        },
        shouldShowProductSyncMessage(product) {
            if (!product || !product.origins || !Array.isArray(product.origins)) {
                return;
            }
            
            if (!this.hasFdsEnabledConfigs()) {
                return;
            }
            
            const syncEnabledOrigins = this.getSyncEnabledOrigins();
            const enabledFdsOrigins = this.getEnabledFdsOrigins();
            const needsSync = product.origins.some(origin => 
                origin.name && syncEnabledOrigins.includes(origin.name.toLowerCase()) && enabledFdsOrigins.includes(origin.name.toLowerCase())
            );
            
            this.showSyncMenuMessage(needsSync);
            return needsSync;
        },
        shouldShowSyncMenuMessage(checkedCategories) {
            if (!checkedCategories?.length || !this.products || typeof this.products !== 'object') return;
            
            if (!this.hasFdsEnabledConfigs()) {
                return;
            }
            
            // Reutilizar el método helper para verificar FDS
            const needsSync = this.checkCategoriesHaveFdsOrigins(checkedCategories);

            this.showSyncMenuMessage(needsSync);
            return needsSync
        },
        // Método helper para reutilizar lógica de verificación FDS
        checkCategoriesHaveFdsOrigins(checkedCategories) {
            if (!checkedCategories?.length || !this.products || typeof this.products !== 'object') {
                return false;
            }

            const syncEnabledOrigins = this.getSyncEnabledOrigins();
            const enabledFdsOrigins = this.getEnabledFdsOrigins();
             return Object.values(this.products)
                .map(productEntry => Array.isArray(productEntry) && productEntry.length > 1 ? productEntry[1] : productEntry)
                .filter(product => product?.department && checkedCategories.includes(product.department))
                .some(product =>
                    product.origins?.length > 0 &&
                    product.origins.some(origin =>
                        origin.name && syncEnabledOrigins.includes(origin.name.toLowerCase()) && enabledFdsOrigins.includes(origin.name.toLowerCase())
                    )
                );
        },
        // Filtra this.products y devuelve los productos cuya categoría (department)
        // esté incluida en categoryNames. Normaliza el shape [key, product] | product.
        getProductsInCategories(categoryNames) {
            if (!Array.isArray(categoryNames) || !categoryNames.length) return [];
            if (!this.products || typeof this.products !== 'object') return [];

            return Object.values(this.products)
                .map(entry => Array.isArray(entry) && entry.length > 1 ? entry[1] : entry)
                .filter(product => product?.department && categoryNames.includes(product.department));
        },
        // Lee las cartas (menues) desde Firebase en listProducts/{productListId}/menues.
        // Devuelve [] si no hay productListId, si el nodo no existe o si falla la lectura
        // (la spec pide no forzar sync cuando no se puede decidir).
        async fetchMenuesForList(productListId) {
            if (!productListId) return [];
            try {
                const snapshot = await get(dbRef(db, `listProducts/${productListId}/menues`));
                const val = snapshot.val();
                if (!val) return [];
                return Array.isArray(val) ? val : Object.values(val);
            } catch (e) {
                console.warn("Error leyendo menues para FDS sync", e);
                return [];
            }
        },
        // Dado un producto y una lista de menues ya cargada, aplica la regla del ticket:
        // "existe al menos una carta que contenga al producto && esa carta tiene al menos
        // un origin FDS (habilitado en la instancia y en el shop)".
        // La relación producto ↔ carta se determina por overlap de origin.name.
        productIsInFdsMenu(product, menues) {
            if (!product || !Array.isArray(menues) || menues.length === 0) return false;

            const productOriginNames = (Array.isArray(product.origins) ? product.origins : [])
                .map(o => o?.name?.toLowerCase().trim())
                .filter(Boolean);
            if (productOriginNames.length === 0) return false;

            const syncEnabledOrigins = this.getSyncEnabledOrigins();
            const enabledFdsOrigins = this.getEnabledFdsOrigins();

            return menues.some(menu => {
                const menuOriginNames = (Array.isArray(menu?.origins) ? menu.origins : [])
                    .map(o => o?.name?.toLowerCase().trim())
                    .filter(Boolean);
                if (menuOriginNames.length === 0) return false;

                const menuContainsProduct = menuOriginNames.some(n => productOriginNames.includes(n));
                if (!menuContainsProduct) return false;

                return menuOriginNames.some(n =>
                    syncEnabledOrigins.includes(n) && enabledFdsOrigins.includes(n)
                );
            });
        },
        // Orquestador usado por los callers que arman el body de:
        //   POST /api/optionProduct/saveOptionProduct
        //   POST /api/option/saveOption
        //   PUT  /api/option/updateOption
        //   DELETE /api/option/deleteOption
        //   POST /api/optionGroup/saveOptionGroup
        //
        // Devuelve true si al menos uno de los productos afectados pertenece a una carta
        // con origin FDS habilitado; false en cualquier otro caso o ante fallo de Firebase.
        async computeShouldSyncWithFds({ productListId, products } = {}) {
            if (!productListId) return false;
            if (!this.hasFdsEnabledConfigs()) return false;

            const affected = Array.isArray(products) ? products.filter(Boolean) : [];
            if (affected.length === 0) return false;

            const menues = await this.fetchMenuesForList(productListId);
            if (!menues.length) return false;

            return affected.some(p => this.productIsInFdsMenu(p, menues));
        },
    }
}
</script>